import { useState } from 'react'
import { useNavigate, Link } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { applicationsApi, jobsApi, candidatesApi } from '@/lib/api'
import { PageLayout } from '@/components/layout/PageLayout'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Card, CardContent } from '@/components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { toast } from '@/components/ui/toaster'

export function NewApplicationPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const [jobId, setJobId] = useState('')
  const [candidateId, setCandidateId] = useState('')
  const [source, setSource] = useState('internal')
  const [coverLetter, setCoverLetter] = useState('')

  const { data: jobsData } = useQuery({
    queryKey: ['jobs', { status: 'published' }],
    queryFn: () => jobsApi.list({ status: 'published' }),
  })
  const jobs = (jobsData as any)?.items || []

  const { data: candidatesData } = useQuery({
    queryKey: ['candidates'],
    queryFn: () => candidatesApi.list(),
  })
  const candidates = (candidatesData as any)?.items || []

  const createApplication = useMutation({
    mutationFn: (data: unknown) => applicationsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['applications'] })
      toast({ title: 'Application created', variant: 'success' })
      navigate({ to: '/applications' })
    },
    onError: (err) => {
      toast({
        title: 'Failed to create application',
        description: err instanceof Error ? err.message : 'Something went wrong',
        variant: 'destructive',
      })
    },
  })

  const canSubmit = jobId && candidateId

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!canSubmit) return
    createApplication.mutate({
      jobId,
      candidateId,
      source,
      coverLetter: coverLetter || undefined,
    })
  }

  return (
    <PageLayout fullHeight back="/applications" title="Add Application" maxWidth="lg">
      <Card className="h-full flex flex-col">
        <CardContent className="flex-1 min-h-0 flex flex-col pt-6">
          <form onSubmit={handleSubmit} className="h-full flex flex-col">
            <div className="flex-1 min-h-0 overflow-y-auto space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="app-job">Job *</Label>
                <Select value={jobId} onValueChange={setJobId}>
                  <SelectTrigger id="app-job">
                    <SelectValue placeholder="Select a job..." />
                  </SelectTrigger>
                  <SelectContent>
                    {jobs.map((job: any) => (
                      <SelectItem key={job.id} value={job.id}>
                        {job.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="app-candidate">Candidate *</Label>
                <Select value={candidateId} onValueChange={setCandidateId}>
                  <SelectTrigger id="app-candidate">
                    <SelectValue placeholder="Select a candidate..." />
                  </SelectTrigger>
                  <SelectContent>
                    {candidates.map((c: any) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.firstName} {c.lastName} — {c.email}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="app-source">Source</Label>
                <Select value={source} onValueChange={setSource}>
                  <SelectTrigger id="app-source">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="internal">Internal</SelectItem>
                    <SelectItem value="referral">Referral</SelectItem>
                    <SelectItem value="linkedin">LinkedIn</SelectItem>
                    <SelectItem value="job_board">Job Board</SelectItem>
                    <SelectItem value="website">Website</SelectItem>
                    <SelectItem value="agency">Agency</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="app-cover">Cover Letter</Label>
                <Textarea
                  id="app-cover"
                  value={coverLetter}
                  onChange={(e) => setCoverLetter(e.target.value)}
                  placeholder="Optional cover letter..."
                  rows={4}
                />
              </div>
            </div>

            <div className="flex-shrink-0 flex justify-end gap-2 pt-3 border-t mt-3">
              <Button type="button" variant="outline" asChild>
                <Link to="/applications">Cancel</Link>
              </Button>
              <Button type="submit" disabled={!canSubmit || createApplication.isPending}>
                {createApplication.isPending ? 'Creating...' : 'Create Application'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </PageLayout>
  )
}
